import React, { useState, useEffect } from 'react';
import Card from '../../common/Card';
import Button from '../../common/Button';
import SearchInput from '../../common/SearchInput';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import RepairCard from '../RepairCard';
import useRepair from '../../../hooks/useRepair';
import './styles.css';

function RepairList({ token, user, warrantyId }) {
  // 使用自定義Hook
  const {
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    fetchRepairs,
    fetchRepairsByWarrantyId,
    groupedRepairs,
    formatDate
  } = useRepair(token);

  // 本地狀態
  const [repairsByWarranty, setRepairsByWarranty] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // 如果傳入了warrantyId，則獲取特定保養單的維修單
  useEffect(() => {
    if (warrantyId) {
      setIsLoading(true);
      setLocalError(null);
      
      fetchRepairsByWarrantyId(warrantyId)
        .then(repairs => {
          console.log("獲取到的維修單:", repairs);
          setRepairsByWarranty(repairs || []);
        })
        .catch(err => {
          console.error("獲取維修單錯誤:", err);
          setLocalError(err.message || '獲取維修單時出錯');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // 如果沒有warrantyId，則使用fetchRepairs獲取所有維修單
      console.log("準備獲取所有維修單");
      fetchRepairs();
    }
  }, [warrantyId, fetchRepairsByWarrantyId, fetchRepairs]);

  // 根據不同模式選擇要顯示的維修單
  const selectedRepairs = warrantyId ? repairsByWarranty : 
    (groupedRepairs[filterStatus] || []);

  // 加載中狀態
  if ((loading && !warrantyId) || (isLoading && warrantyId)) {
    return <Loading text="正在加載維修單數據..." />;
  }

  // 錯誤狀態
  if ((error && !warrantyId) || (localError && warrantyId)) {
    return <ErrorMessage message={error || localError} />;
  }

  // 按狀態過濾的選項（僅在全部模式下顯示）
  const renderStatusFilter = () => {
    if (warrantyId) return null;

    return (
      <div className="repair-list-filter">
        <Button
          className={filterStatus === 'Pending' ? 'active' : ''}
          onClick={() => setFilterStatus('Pending')}
        >
          待處理
        </Button>
        <Button
          className={filterStatus === 'In Progress' ? 'active' : ''}
          onClick={() => setFilterStatus('In Progress')}
        >
          處理中
        </Button>
        <Button
          className={filterStatus === 'Completed' ? 'active' : ''}
          onClick={() => setFilterStatus('Completed')}
        >
          已完成
        </Button>
        <Button
          className={filterStatus === 'Cancelled' ? 'active' : ''}
          onClick={() => setFilterStatus('Cancelled')}
        >
          已取消
        </Button>
      </div>
    );
  };

  return (
    <div className="repair-list-container">
      {!warrantyId && (
        <div className="repair-list-header">
          <h2 className="repair-list-title">維修單管理</h2>
          <div className="repair-list-actions">
            <SearchInput
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="搜索維修單..."
              className="repair-search-input"
            />
            <Button onClick={fetchRepairs} className="refresh-btn">
              刷新
            </Button>
          </div>
        </div>
      )}

      {renderStatusFilter()}

      <div className="repair-list-section">
        <Card className="repair-list-card">
          {!warrantyId ? (
            <h3 className="repair-list-section-title">
              {filterStatus === 'Pending' && '待處理維修單'}
              {filterStatus === 'In Progress' && '處理中維修單'}
              {filterStatus === 'Completed' && '已完成維修單'}
              {filterStatus === 'Cancelled' && '已取消維修單'}
              <span className="repair-list-count">
                ({selectedRepairs.length})
              </span>
            </h3>
          ) : (
            <h3 className="repair-list-section-title">
              相關維修單
              <span className="repair-list-count">
                ({selectedRepairs.length})
              </span>
            </h3>
          )}

          {selectedRepairs.length === 0 ? (
            <div className="repair-list-empty">
              {!warrantyId ? (
                <p>暫無{filterStatus === 'Pending' ? '待處理' : 
                  filterStatus === 'In Progress' ? '處理中' : 
                  filterStatus === 'Completed' ? '已完成' : '已取消'}
                維修單</p>
              ) : (
                <p>此保養單暫無相關維修單</p>
              )}
            </div>
          ) : (
            <div className="repair-list-grid">
              {selectedRepairs.map((repair) => (
                <RepairCard
                  key={repair.id}
                  repair={repair}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default RepairList; 